import type { LocationFilter } from "generated/graphql";
import { useCallback, useMemo } from "react";
import { usePersistedReducer } from "src/utils/hooks/persisted-reducer";
import { countLocationFilters } from "./count-filters";
import {
  FilterActionTypes,
  type LocationFilterState,
  defaultState,
  filterReducer,
} from "./filter-reducer";
import { flattenGeometryFilter } from "./geometry-filter/flatten-geometry-filter";
import type { LocationFilterType } from "./types";

export const useLocationFilters = (args?: {
  initialState?: Partial<LocationFilterState>;
  persistenceKey?: string;
}) => {
  const { initialState, persistenceKey } = args ?? {};
  const [filters, dispatchFilters] = usePersistedReducer(
    filterReducer,
    {
      ...defaultState,
      ...initialState,
    },
    persistenceKey,
  );

  const preparedFilters = useMemo<LocationFilter>(
    () => ({
      and: filters.and.map(({ id: _, ...rest }) => ({
        geometry: flattenGeometryFilter(rest.geometry),
      })),
    }),
    [filters],
  );

  const resetFilters = useCallback(
    (filterType?: LocationFilterType) => {
      if (filterType == null || typeof filterType !== "string") {
        dispatchFilters({
          type: FilterActionTypes.RESET_FILTERS,
          payload: initialState,
        });
      } else {
        dispatchFilters({
          type: FilterActionTypes.RESET_FILTER_TYPE,
          payload: filterType,
        });
      }
    },
    [initialState, dispatchFilters],
  );

  const filterCounts = useMemo(
    () => countLocationFilters(preparedFilters),
    [preparedFilters],
  );

  return {
    filters,
    preparedFilters,
    filterCounts,
    resetFilters,
    dispatchFilters,
  };
};

export type LocationFilters = ReturnType<typeof useLocationFilters>;
